import { useContext, useMemo } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import { PanelSection } from '../SidePanel';
import CropIcon from '../icons/CropIcon';
import { SquareSwatch } from '../right-menu/SquareSwatch';
import { AREA_UNITS, DISTANCE_UNITS } from '../../constants/units';
import { useUnits } from '../../contexts/UnitContext';
import {
  asDynamicDecimal,
  asQuantityWithUnit,
  asRoundedDecimal,
} from '../../utils/formatters';
import { colorForCrop, cropThemes } from '../../utils/colors';
import { useFarmMeta } from './utils/content';
import { PANEL_INNER_HEIGHT } from './constants';
import { FieldsPreviewContext, SectionHeader } from './components';
import { MapContext } from '../../contexts/MapContext';

// Indented to match the indentation of the crop name
const IndentedCropDetails = styled.div`
  margin-left: 28px;
`;

const ROW_HEIGHT = 40;
const ROW_MARGIN = 8;

const Row = ({
  data: { crop, cropId, treeCount, acreage },
  formattedSpacingInfo,
  transformArea,
  areaUnitName,
}) => {
  const { name } = crop;

  const formattedTreeCount = asQuantityWithUnit('stems', 'stem')(treeCount);

  const formattedArea = `${asDynamicDecimal(
    transformArea(acreage || 0),
  )} ${areaUnitName}`;

  const formattedSpacing = formattedSpacingInfo[cropId];

  const cropDetails = [formattedTreeCount, formattedArea, formattedSpacing]
    .filter(Boolean)
    .join(' | ');

  return (
    <div key={cropId} style={{ height: ROW_HEIGHT, marginBottom: ROW_MARGIN }}>
      <div className="d-flex align-items-center mb-1">
        <SquareSwatch
          color={colorForCrop(crop, cropThemes.LIGHT)}
          borderColor={colorForCrop(crop, cropThemes.DARK)}
        />
        <strong className="ml-3 text-truncate">{name}</strong>
      </div>

      <IndentedCropDetails className="text-truncate">
        {cropDetails}
      </IndentedCropDetails>
    </div>
  );
};

/**
 * Returns a mapping of `cropId` to a formatted string of the spacing info for that crop (or `null` if no data.)
 */
export const useFormattedSpacingInfo = () => {
  const {
    patterns: { inRowPatternsMap },
    fields: { fieldSections },
  } = useContext(MapContext);

  const { transformToUnit, displayUnit } = useUnits(DISTANCE_UNITS);

  return useMemo(() => {
    const result = {};

    // string we use for crops that are used w/ more than one spacing:
    const VARIABLE = 'variable';

    const format = (length) =>
      asRoundedDecimal(transformToUnit(length), { digits: 1 });

    const setSpacing = (cropId, inRow, betweenRow) => {
      const formatted = `${format(betweenRow)}x${format(inRow)} ${displayUnit}`;

      if (result[cropId]) {
        // There's already a spacing result here. If it matches the new one, we're good.
        if (result[cropId] === formatted) return;

        // If not, that means there are multiple spacings, so we mark it as 'variable'
        result[cropId] = VARIABLE;
      } else {
        // First time we've seen this crop, set the spacing:
        result[cropId] = formatted;
      }
    };

    fieldSections.forEach(
      ({ inRowSpacing, betweenRowSpacing, crop, primaryInRowPattern }) => {
        if (primaryInRowPattern) {
          // There's not usually a way to get a single spacing number when a
          // crop is used in a pattern (since left and right spacing are
          // typically different), so we mark the crops in this pattern as
          // having variable spacings.
          const pattern = inRowPatternsMap[primaryInRowPattern.id];
          if (pattern) {
            pattern.cropSequence.forEach(({ crop: { id } }) => {
              result[id] = VARIABLE;
            });
          }
        } else if (crop) {
          // Add spacing for the main section crop
          setSpacing(crop.id, inRowSpacing, betweenRowSpacing);
        }
      },
    );

    return result;
  }, [fieldSections, inRowPatternsMap, displayUnit, transformToUnit]);
};

export const useCropsInfo = () => {
  const { isPrintMode, togglePanelVisibility, panelVisibility } = useContext(
    FieldsPreviewContext,
  );

  const {
    transformToUnit: transformArea,
    displayUnit: areaUnitName,
  } = useUnits(AREA_UNITS);

  const { cropData } = useFarmMeta();

  const formattedSpacingInfo = useFormattedSpacingInfo();

  const rowsPerPanel = Math.floor(
    PANEL_INNER_HEIGHT / (ROW_HEIGHT + ROW_MARGIN),
  );

  const chunks = _.chunk(cropData, rowsPerPanel);

  const header = (
    <SectionHeader
      title="Crops"
      icon={<CropIcon />}
      onClick={() => togglePanelVisibility('crops')}
    />
  );

  if (!panelVisibility.crops) {
    if (isPrintMode) return [];

    return [
      <PanelSection className="opacity-50" key="crops-hidden">
        {header}
      </PanelSection>,
    ];
  }

  return chunks.map((chunk, i) => (
    <PanelSection
      $hideBottomBorder={i !== chunks.length - 1}
      key={chunk.map(({ cropId }) => cropId).join('-')}
    >
      {i === 0 && header}

      {chunk.map((data) => (
        <Row
          key={data.cropId}
          data={data}
          transformArea={transformArea}
          areaUnitName={areaUnitName}
          formattedSpacingInfo={formattedSpacingInfo}
        />
      ))}
    </PanelSection>
  ));
};
